import { CONCEPTS, type ConceptId } from "../data/concepts";
import { FIGURES, type FigureId } from "../data/figures";
import { analects } from "./analects";
import type { Analect } from "./types";

/**
 * Passage → concepts. This is what lets a concept-level authored item be
 * served for a passage that has no passage-specific item of its own: the
 * 致用 gate on 里仁 4.5 can draw an authored 義利 application question even
 * though nobody wrote one for 4.5 in particular.
 *
 * Three sources, in priority order:
 *   1. the concept taxonomy's own `keyPassages` (hand-picked loci),
 *   2. `OVERRIDES` for passages the keyword pass gets wrong,
 *   3. weighted keyword matching over text + translation + annotations.
 */

/** Passages the keyword pass misreads badly enough to be worth naming. */
const OVERRIDES: Record<number, ConceptId[]> = {
  130: ["tianming", "ren"], // 伯牛有疾 — grief and fate, not "study habits"
  148: ["tianming", "chushi"], // 子見南子
  160: ["li", "ren"], // 是日哭則不歌
  171: ["tianming"], // 子不語怪力亂神
  173: ["tianming", "chushi"], // 天生德於予
  185: ["tianming", "li"], // 子疾病，子路請禱
  213: ["junzi", "zhiren"], // 子絕四
  219: ["li", "ren"], // 見齊衰者必作
  226: ["xue", "tianming"], // 逝者如斯夫
  237: ["junzi", "chushi"], // 歲寒然後知松柏
  241: ["li", "junzi"], // 孔子於鄉黨
  257: ["ren", "li"], // 廄焚，不問馬
  262: ["ren", "xin"], // 朋友死，無所歸
  279: ["tianming", "jiaoxue"], // 顏淵死，天喪予
  280: ["li", "ren"], // 子哭之慟
  281: ["li", "jiaoxue"], // 門人厚葬顏淵
  282: ["tianming", "li"], // 未能事人，焉能事鬼
  305: ["tianming", "jiaoxue"],
  327: ["tianming", "junzi"], // 死生有命，富貴在天
  411: ["tianming", "zhiren"], // 莫我知也夫
  414: ["chushi", "tianming"], // 知其不可而為之
  422: ["junzi", "chushi"], // 在陳絕糧
  474: ["yili", "junzi"], // 齊景公有馬千駟
  489: ["zhongyong", "junzi"], // 鄉愿，德之賊也
  503: ["ren", "chushi"], // 殷有三仁
  507: ["chushi"], // 楚狂接輿
  508: ["chushi"], // 長沮桀溺
  509: ["chushi", "li"], // 荷蓧丈人
  513: ["shijiao"], // 周有八士
  541: ["tianming", "li", "zhiren"], // 不知命無以為君子
};

const KEYWORDS: Record<ConceptId, Array<[string, number]>> = {
  ren: [["仁", 3], ["愛人", 3], ["恕", 2], ["克己", 3], ["不忍", 2], ["惻", 2]],
  li: [["禮", 3], ["礼", 3], ["祭", 2], ["喪", 2], ["丧", 2], ["敬", 1], ["儀", 2], ["八佾", 2], ["樂", 1], ["乐", 1]],
  xue: [["學", 3], ["学", 3], ["習", 2], ["习", 2], ["思", 2], ["問", 1], ["问", 1], ["溫故", 3], ["温故", 3], ["好學", 3], ["好学", 3], ["知新", 2]],
  junzi: [["君子", 3], ["小人", 2], ["成人", 2], ["士", 1], ["器", 1]],
  xiao: [["孝", 3], ["悌", 2], ["弟子入則孝", 3], ["父", 2], ["母", 2], ["兄", 1], ["三年之喪", 3], ["三年之丧", 3]],
  shu: [["忠恕", 4], ["一以貫之", 4], ["一以贯之", 4], ["己所不欲", 4], ["三省", 3], ["推己", 3]],
  xin: [["信", 3], ["朋友", 2], ["諾", 2], ["诺", 2], ["言而有信", 4], ["無信", 3], ["无信", 3]],
  yili: [["義", 3], ["义", 3], ["利", 3], ["富", 2], ["貴", 2], ["贵", 2], ["貧", 1], ["贫", 1], ["恥", 1], ["耻", 1]],
  zheng: [["政", 3], ["民", 2], ["刑", 2], ["治", 2], ["邦", 1], ["君", 1], ["臣", 1], ["為政", 4], ["为政", 4], ["賦", 1], ["赋", 1]],
  zhengming: [["正名", 5], ["名不正", 5], ["君君", 4], ["觚", 4], ["八佾舞", 3]],
  zhiren: [["知人", 4], ["觀其", 3], ["观其", 3], ["察", 2], ["視其所以", 4], ["视其所以", 4], ["賢", 1], ["贤", 1], ["毀譽", 3], ["毁誉", 3], ["譖", 2]],
  zhongyong: [["中庸", 5], ["過猶不及", 5], ["过犹不及", 5], ["和而不同", 4], ["鄉愿", 4], ["乡愿", 4], ["狂狷", 3], ["和為貴", 3], ["和为贵", 3]],
  tianming: [["天命", 4], ["命", 2], ["鬼神", 3], ["天", 2], ["死", 2], ["疾", 1], ["禱", 3], ["祷", 3], ["怪力亂神", 4], ["怪，力，亂，神", 4]],
  wenzhi: [["文質", 5], ["文质", 5], ["彬彬", 4], ["質勝文", 5], ["质胜文", 5], ["繪事後素", 4], ["绘事后素", 4], ["虎豹之鞟", 4]],
  shijiao: [["詩", 3], ["诗", 3], ["韶", 3], ["雅", 2], ["頌", 2], ["颂", 2], ["關雎", 4], ["关雎", 4], ["興於詩", 4], ["兴于诗", 4], ["樂而不淫", 4]],
  chushi: [["隱", 3], ["隐", 3], ["仕", 3], ["邦有道", 4], ["邦無道", 4], ["邦无道", 4], ["用之則行", 4], ["用之则行", 4], ["辟世", 4], ["不在其位", 3]],
  jiaoxue: [["誨", 3], ["诲", 3], ["教", 3], ["啟", 2], ["启", 2], ["有教無類", 5], ["有教无类", 5], ["因材", 4], ["舉一隅", 4], ["举一隅", 4], ["門人", 1], ["门人", 1]],
};

const FIGURE_ALIASES: Record<FigureId, string[]> = {
  kongzi: ["孔子", "夫子", "仲尼"],
  yanhui: ["顏回", "颜回", "顏淵", "颜渊", "回也"],
  zilu: ["子路", "仲由", "季路", "由也"],
  zigong: ["子貢", "子贡", "端木賜", "賜也", "赐也"],
  zengzi: ["曾子", "曾參", "曾参", "參乎", "参乎"],
  zixia: ["子夏", "卜商", "商也"],
  ziyou: ["子游", "言偃"],
  ranyou: ["冉有", "冉求", "求也", "冉子"],
  zaiwo: ["宰我", "宰予"],
  zizhang: ["子張", "子张", "師也", "师也"],
  minziqian: ["閔子騫", "闵子骞", "閔子", "闵子"],
  zhonggong: ["仲弓", "冉雍", "雍也可使"],
  fanchi: ["樊遲", "樊迟"],
  guanzhong: ["管仲", "管氏"],
  boyi: ["伯夷", "叔齊", "叔齐"],
};

function buildKeyPassageIndex(): Map<number, ConceptId[]> {
  const index = new Map<number, ConceptId[]>();
  for (const concept of CONCEPTS) {
    for (const id of concept.keyPassages) {
      const list = index.get(id) ?? [];
      if (!list.includes(concept.id)) list.push(concept.id);
      index.set(id, list);
    }
  }
  return index;
}

const KEY_PASSAGES = buildKeyPassageIndex();

function inferConcepts(analect: Analect): ConceptId[] {
  const haystack = `${analect.text}\n${analect.translation}`;
  const scored = CONCEPTS.map((concept) => {
    let score = 0;
    for (const [word, weight] of KEYWORDS[concept.id]) {
      if (haystack.includes(word)) score += weight;
    }
    return { id: concept.id, score };
  })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return ["junzi"];
  const top = scored[0].score;
  return scored.filter((entry) => entry.score >= Math.max(3, top * 0.6)).slice(0, 3).map((entry) => entry.id);
}

const conceptCache = new Map<number, ConceptId[]>();

export function conceptsOf(analect: Analect): ConceptId[] {
  const cached = conceptCache.get(analect.id);
  if (cached) return cached;

  const explicit = OVERRIDES[analect.id];
  const keyed = KEY_PASSAGES.get(analect.id) ?? [];
  const merged = explicit ?? (keyed.length ? keyed : inferConcepts(analect));
  const result = Array.from(new Set([...merged, ...keyed])).slice(0, 3);
  conceptCache.set(analect.id, result);
  return result;
}

const figureCache = new Map<number, FigureId[]>();

export function figuresOf(analect: Analect): FigureId[] {
  const cached = figureCache.get(analect.id);
  if (cached) return cached;
  const haystack = `${analect.title}\n${analect.text}\n${analect.translation}`;
  const found: FigureId[] = [];
  for (const [id, aliases] of Object.entries(FIGURE_ALIASES) as Array<[FigureId, string[]]>) {
    if (id === "kongzi") continue; // present almost everywhere; not a useful tag
    if (aliases.some((alias) => haystack.includes(alias))) found.push(id);
  }
  figureCache.set(analect.id, found);
  return found;
}

/** concept id → every passage carrying it. Built once, drives the knowledge map. */
export const PASSAGES_BY_CONCEPT: ReadonlyMap<ConceptId, number[]> = (() => {
  const map = new Map<ConceptId, number[]>();
  for (const concept of CONCEPTS) map.set(concept.id, []);
  for (const analect of analects) {
    for (const id of conceptsOf(analect)) map.get(id)!.push(analect.id);
  }
  return map;
})();

export const PASSAGES_BY_FIGURE: ReadonlyMap<FigureId, number[]> = (() => {
  const map = new Map<FigureId, number[]>();
  for (const figure of FIGURES) map.set(figure.id, []);
  for (const analect of analects) {
    for (const id of figuresOf(analect)) map.get(id)!.push(analect.id);
  }
  for (const figure of FIGURES) {
    const list = map.get(figure.id)!;
    for (const id of figure.keyPassages) if (!list.includes(id)) list.push(id);
    list.sort((a, b) => a - b);
  }
  return map;
})();
